export const getStarts = (totalTurbines, trancheSize = 30) => {
  const starts = [];
  let remaining = totalTurbines;
  while (remaining > 0) {
    starts.push(Math.min(trancheSize, remaining));
    remaining -= trancheSize;
  }
  return starts;
};

export const convolve = (starts, profile) => {
  const result = Array(starts.length + profile.length - 1).fill(0);
  for (let s = 0; s < starts.length; s++) {
    for (let p = 0; p < profile.length; p++) {
      result[s + p] += starts[s] * profile[p];
    }
  }
  return result;
};

// Unit DEVEX lookup function based on total programme size (economies of scale)
const getUnitDevex = (totalTurbines) => {
  if (totalTurbines <= 10) return 645000;
  if (totalTurbines <= 20) return 628333;
  if (totalTurbines <= 30) return 611667;
  if (totalTurbines <= 40) return 595000;
  if (totalTurbines <= 50) return 578333;
  if (totalTurbines <= 60) return 561667;
  if (totalTurbines <= 70) return 545000;
  if (totalTurbines <= 80) return 528333;
  if (totalTurbines <= 90) return 511667;
  // 91-125 turbines use the 100-turbine rate
  return 495000;
};

export const calculateMetrics = (turbineCount, config) => {
  const { per_turbine, phasing, index, impact } = config;
  
  // Calculate tranche starts
  const starts = getStarts(turbineCount, phasing.tranche_size);
  
  // Calculate per-turbine DEVEX with economies of scale based on total programme size
  const unitDevex = getUnitDevex(turbineCount);
  const devexProfileScaled = phasing.profile.map(p => p * unitDevex);
  const annualDevexSpend = convolve(starts, devexProfileScaled);
  
  // Calculate programme duration (last year with non-zero spend + 1 for 1-indexed)
  const lastActiveYear = annualDevexSpend.findLastIndex(spend => spend > 0.01);
  const programmeDuration = lastActiveYear + 1;
  
  // Extend arrays to minimum display length (10 years)
  const minDisplayYears = 10;
  const displayYears = Math.max(minDisplayYears, programmeDuration);
  while (annualDevexSpend.length < displayYears) {
    annualDevexSpend.push(0);
  }
  
  // Calculate detailed phasing table with recycling
  // From the spreadsheet: turbines that start in Year 1 are "installed" (operational) in Year 4
  // This is 3 years after they start (Year 1 + 3 = Year 4)
  const profileLength = phasing.profile.length; // 4 years
  
  const phasingTable = [];
  
  for (let year = 0; year < displayYears; year++) {
    const started = year < starts.length ? starts[year] : 0;
    
    // Calculate installed: turbines that started (profileLength - 1) years ago
    // Year 1 start → Year 4 installed means year >= 3 and look back 3 years
    const installed = year >= (profileLength - 1) && year - (profileLength - 1) < starts.length 
      ? starts[year - (profileLength - 1)] 
      : 0;
    
    // Calculate in development at end of year
    // Turbines remain "in development" from when they start until they're installed
    let inDevelopmentEOY = 0;
    for (let i = 0; i <= year && i < starts.length; i++) {
      const yearsFromStart = year - i;
      // In development if started but not yet installed (< profileLength - 1)
      if (yearsFromStart < (profileLength - 1)) {
        inDevelopmentEOY += starts[i];
      }
    }
    
    // Annual development budget (from convolution)
    const annualDevelopment = annualDevexSpend[year] || 0;
    
    // Recycled DEVEX - from spreadsheet, this is the full per-turbine DEVEX recovered
    // Recycling happens profileLength years after turbines start (one year after installation)
    // Year 1 start → Year 5 recycled (4 years later)
    const recycledDevex = year >= profileLength && year - profileLength < starts.length
      ? starts[year - profileLength] * unitDevex
      : 0;
    
    // Net funding (development - recycled)
    const netFunding = annualDevelopment - recycledDevex;
    
    phasingTable.push({
      year: year + 1,
      started,
      installed,
      inDevelopmentEOY,
      annualDevelopment,
      recycledDevex,
      netFunding
    });
  }
  
  // Calculate cumulative net funding
  let cumulativeNet = 0;
  phasingTable.forEach(row => {
    cumulativeNet += row.netFunding;
    row.cumulativeNetFunding = cumulativeNet;
  });
  
  // Calculate cumulative funding for charts (gross, not net)
  const cumulativeFunding = annualDevexSpend.reduce((acc, val) => {
    const prev = acc.length > 0 ? acc[acc.length - 1] : 0;
    acc.push(prev + val);
    return acc;
  }, []);
  
  // Calculate total DEVEX with economies of scale based on total programme size
  const totalDevex = unitDevex * turbineCount;
  
  // Calculate community benefits (indexed annually over display years)
  const annualCommunityBenefit = per_turbine.annual_benefit * turbineCount;
  const communityBenefitGrowth = Array.from({ length: displayYears }, (_, i) => 
    annualCommunityBenefit * Math.pow(1 + index.annual_rate, i)
  );
  
  // Calculate lifetime community benefit using the corrected per-turbine profile
  // These values already include debt repayment modeling and indexation - do not apply additional indexing
  const communityFundProfile = per_turbine.communityFundPerTurbine || [];
  
  // Calculate accurate Total Benefit (Year 30) with phasing
  // This matches what's shown in the Community Value Generated chart
  const calculateTotalBenefitWithPhasing = () => {
    const lifecycleYears = 30;
    let cumulativeFund = 0;
    let cumulativeLandRent = 0;
    let cumulativeBusinessRates = 0;
    
    // Track operational turbines by year
    const turbinesByOperationalYear = [];
    for (let year = 0; year < phasingTable.length; year++) {
      if (phasingTable[year].installed > 0) {
        turbinesByOperationalYear.push({
          year,
          count: phasingTable[year].installed
        });
      }
    }
    
    const firstOperationalYear = turbinesByOperationalYear.length > 0 ? turbinesByOperationalYear[0].year : 0;
    
    for (let year = 0; year < lifecycleYears; year++) {
      // Community fund (starts year after installation)
      let annualFund = 0;
      for (const tranche of turbinesByOperationalYear) {
        const yearsOperational = year - tranche.year;
        if (yearsOperational >= 1 && yearsOperational < communityFundProfile.length) {
          const profileValue = communityFundProfile[yearsOperational] || 0;
          annualFund += profileValue * tranche.count;
        }
      }
      
      // Business rates and land rent (start year of installation)
      let operationalTurbines = 0;
      for (const tranche of turbinesByOperationalYear) {
        if (tranche.year <= year) {
          operationalTurbines += tranche.count;
        }
      }
      
      if (operationalTurbines > 0) {
        const yearsOperational = year - firstOperationalYear;
        const indexMultiplier = yearsOperational >= 0 ? Math.pow(1 + index.annual_rate, yearsOperational) : 1;
        
        const incomeFraction = operationalTurbines / turbineCount;
        const annualBusinessRates = (impact.business_rates_per_mw_per_year * per_turbine.capacity_mw * turbineCount) * incomeFraction * indexMultiplier;
        const annualLandRent = (impact.land_rent_per_turbine_per_year * turbineCount) * incomeFraction * indexMultiplier;
        
        cumulativeBusinessRates += annualBusinessRates;
        cumulativeLandRent += annualLandRent;
      }
      
      cumulativeFund += annualFund;
    }
    
    return cumulativeFund + cumulativeBusinessRates + cumulativeLandRent;
  };
  
  const totalBenefitYear30 = calculateTotalBenefitWithPhasing();
  const lifetimeCommunityBenefit = communityFundProfile.reduce((sum, val) => sum + val, 0) * turbineCount;
  
  // Calculate power generation
  const annualGenerationMWh = per_turbine.annual_gen_mwh * turbineCount;
  const annualGenerationGWh = annualGenerationMWh / 1000;
  const lifetimeGenerationGWh = annualGenerationGWh * per_turbine.lifetime_years;
  
  // Calculate CO2 avoided
  const annualCO2Tonnes = annualGenerationMWh * impact.co2_tonnes_per_mwh;
  const lifetimeCO2Kilotonnes = (annualCO2Tonnes * per_turbine.lifetime_years) / 1000;
  
  // Calculate jobs (FTE)
  const jobsFTE = per_turbine.jobs_fte * turbineCount;
  
  // Calculate leveraged capital (CAPEX per turbine)
  const leveragedCapital = per_turbine.capex * turbineCount;
  
  // Calculate business rates (annual)
  const annualBusinessRates = impact.business_rates_per_mw_per_year * per_turbine.capacity_mw * turbineCount;
  // Apply 3% indexation over lifetime
  const lifetimeBusinessRates = Array.from({ length: per_turbine.lifetime_years }, (_, i) => 
    annualBusinessRates * Math.pow(1 + index.annual_rate, i)
  ).reduce((sum, val) => sum + val, 0);
  
  // Calculate land rent (annual)
  const annualLandRent = impact.land_rent_per_turbine_per_year * turbineCount;
  // Apply 3% indexation over lifetime
  const lifetimeLandRent = Array.from({ length: per_turbine.lifetime_years }, (_, i) => 
    annualLandRent * Math.pow(1 + index.annual_rate, i)
  ).reduce((sum, val) => sum + val, 0);
  
  // Calculate homes powered
  const homesPowered = annualGenerationMWh * 0.25; // ~4,000 kWh per home per year
  
  return {
    turbineCount,
    totalDevex,
    unitDevex, // Per-turbine DEVEX with economies of scale
    programmeDuration,
    displayYears,
    totalBenefitYear30, // Accurate total benefit at Year 30 with phasing
    lifetimeCommunityBenefit,
    cumulativeFunding: cumulativeFunding[cumulativeFunding.length - 1] || 0,
    annualDevexSpend,
    cumulativeFundingArray: cumulativeFunding,
    communityBenefitGrowth,
    phasingTable, // Full phasing table with all columns
    communityFundProfile: communityFundProfile, // Pass the 25-year per-turbine profile to chart
    
    // Power generation
    annualGenerationMWh,
    annualGenerationGWh,
    lifetimeGenerationGWh,
    
    // CO2 avoided
    annualCO2Tonnes,
    lifetimeCO2Kilotonnes,
    
    // Jobs
    jobsFTE,
    
    // Financial impacts
    leveragedCapital,
    annualBusinessRates,
    lifetimeBusinessRates,
    annualLandRent,
    lifetimeLandRent,
    annualCommunityBenefit,
    
    // Homes powered
    homesPowered,
    
    // Tranche info
    starts,
    activeYears: lastActiveYear + 1
  };
};

export const formatCurrency = (value, decimals = 0) => {
  const isNegative = value < 0;
  const absValue = Math.abs(value);
  
  let formatted;
  if (absValue >= 1000000000) {
    formatted = `£${(absValue / 1000000000).toFixed(1)}bn`;
  } else if (absValue >= 1000000) {
    formatted = `£${(absValue / 1000000).toFixed(decimals === 0 ? 1 : decimals)}m`;
  } else if (absValue >= 1000) {
    formatted = `£${(absValue / 1000).toFixed(decimals)}k`;
  } else {
    formatted = `£${absValue.toLocaleString('en-GB')}`;
  }
  
  return isNegative ? `-${formatted}` : formatted;
};

export const formatNumber = (value, decimals = 0) => {
  return value.toLocaleString('en-GB', { maximumFractionDigits: decimals, minimumFractionDigits: decimals });
};

export const formatCO2 = (value) => {
  if (value >= 1000) {
    return `${formatNumber(value / 1000, 1)} ktCO₂`;
  }
  return `${formatNumber(value, 0)} tCO₂`;
};

export const formatPower = (value) => {
  if (value >= 1000) {
    return `${formatNumber(value / 1000, 1)} GWh`;
  }
  return `${formatNumber(value, 0)} MWh`;
};
